import { faCircleInfo, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "antd";
import type React from "react";
import { useRef, useState } from "react";
import styled from "styled-components";

const FieldWrapper = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

const LabelRow = styled.label`
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-weight: 700;
  font-size: 16px;
  cursor: text;
  text-align: left;
`;

const RequiredMark = styled.span`
  color: #d32f2f;
`;

const InputRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding: 8px 12px;
  border: 1px solid var(--color-primary-desat);
  border-radius: 6px;
  min-height: 42px;
  transition: all 0.25s;

  &:focus-within {
    border-color: var(--color-primary-dark);
  }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  font-size: 0.9rem;
`;

const RemoveTagButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  color: var(--color-primary-desat);
  font-size: 0.75rem;

  &:hover {
    color: #d32f2f;
  }
`;

const StyledInput = styled.input`
  flex: 1;
  min-width: 80px;
  border: none;
  outline: none;
  font-size: 16px;

  &::placeholder {
    color: var(--color-primary-desat);
  }
`;

export interface MultiNumberFieldProps {
  label: string;
  name: string;
  value: number[];
  onChange: (value: number[]) => void;
  required?: boolean;
  tooltip?: string;
  placeholder?: string;
}

const MultiNumberField: React.FC<MultiNumberFieldProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  tooltip,
  placeholder = "Type a number and press Enter",
}) => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const numbers = Array.isArray(value) ? value : [];

  const parseAndAdd = (str: string): number | null => {
    const trimmed = str.trim();
    if (!trimmed) return null;
    const num = parseFloat(trimmed);
    if (Number.isNaN(num)) return null;
    return num;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const num = parseAndAdd(inputValue);
      if (num !== null) {
        onChange([...numbers, num]);
        setInputValue("");
      }
    } else if (e.key === "Backspace" && !inputValue && numbers.length > 0) {
      onChange(numbers.slice(0, -1));
    }
  };

  const handleBlur = () => {
    const num = parseAndAdd(inputValue);
    if (num !== null) {
      onChange([...numbers, num]);
      setInputValue("");
    }
  };

  const removeNumber = (index: number) => {
    onChange(numbers.filter((_, i) => i !== index));
  };

  return (
    <FieldWrapper>
      <LabelRow htmlFor={name}>
        {label}
        {required && <RequiredMark>*</RequiredMark>}
        {tooltip && (
          <Tooltip title={tooltip}>
            <span style={{ display: "inline-flex", cursor: "help" }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{
                  color: "var(--color-primary-desat)",
                  fontSize: "0.9rem",
                }}
              />
            </span>
          </Tooltip>
        )}
      </LabelRow>
      <InputRow onClick={() => inputRef.current?.focus()}>
        {numbers.map((num, index) => (
          <Tag key={num}>
            {num}
            <RemoveTagButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeNumber(index);
              }}
              aria-label={`Remove ${num}`}
            >
              <FontAwesomeIcon icon={faTimes} />
            </RemoveTagButton>
          </Tag>
        ))}
        <StyledInput
          ref={inputRef}
          id={name}
          type="text"
          inputMode="decimal"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={numbers.length === 0 ? placeholder : ""}
          aria-required={required}
        />
      </InputRow>
    </FieldWrapper>
  );
};

export default MultiNumberField;
